import { defineStore } from "pinia"
import { computed, ref } from "vue"

import { HttpError, setAuthTokenGetter } from "@/shared/api/http-client"
import { decodeJwtPayload, tokenExpired } from "@/shared/lib/jwt"

import { authGoogle, fetchMe, selectOrg } from "../api/session-api"
import type { AuthUser, Claims } from "./types"

const TOKEN_KEY = "meditation.token"

export const useSessionStore = defineStore("session", () => {
  const token = ref<string | null>(null)
  const user = ref<AuthUser | null>(null)
  const claims = ref<Claims | null>(null)

  const isAuthenticated = computed(() => Boolean(token.value))
  const hasActiveOrg = computed(() => Boolean(token.value && decodeJwtPayload(token.value)?.org_id))

  // Wires this store as the http-client's bearer source (re-registered per store instance; fine for the single-Pinia app).
  setAuthTokenGetter(() => token.value)

  function persist(value: string | null): void {
    token.value = value
    if (value) {
      localStorage.setItem(TOKEN_KEY, value)
    } else {
      localStorage.removeItem(TOKEN_KEY)
    }
  }

  function hydrate(): void {
    const stored = localStorage.getItem(TOKEN_KEY)
    if (stored) {
      token.value = stored
    }
    dropIfExpired()
  }

  function dropIfExpired(): void {
    if (token.value && tokenExpired(token.value)) {
      logout()
    }
  }

  function logout(): void {
    user.value = null
    claims.value = null
    persist(null)
  }

  async function login(idToken: string): Promise<void> {
    const response = await authGoogle(idToken)
    user.value = response.user
    persist(response.token)
  }

  async function loadMe(): Promise<void> {
    try {
      claims.value = await fetchMe()
    } catch (err) {
      if (err instanceof HttpError && err.status === 401) {
        logout()
        return
      }
      throw err
    }
  }

  async function chooseOrg(orgId: string): Promise<void> {
    const response = await selectOrg(orgId)
    persist(response.token)
    // If the re-minted token is rejected, loadMe() logs out; chooseOrg resolves and the store state (token/claims now null) reflects it reactively.
    await loadMe()
  }

  return {
    token,
    user,
    claims,
    isAuthenticated,
    hasActiveOrg,
    hydrate,
    dropIfExpired,
    login,
    loadMe,
    chooseOrg,
    logout,
  }
})
